import { type QueryStatus } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { RiskSeverity } from '@/types';

export interface RiskCountsResult {
  [RiskSeverity.Critical]: number;
  [RiskSeverity.High]: number;
  [RiskSeverity.Medium]: number;
  [RiskSeverity.Low]: number;
  [RiskSeverity.Info]: number;
  total: number;
}

export function useCustomerRiskCounts() {
  // Open (Demonstrated) risks
  const openQuery = useMy(
    {
      resource: 'statistic',
      query: 'my#status:O#now',
      desc: true,
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  // Resolved (Remediated) risks
  const resolvedQuery = useMy(
    {
      resource: 'statistic',
      query: 'my#status:R#now',
      desc: true,
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  // Invalidate when any statistic is updated via WebSocket.
  // Statistics are written to the account partition, so use the
  // account-scoped connection (user: false).
  useRealtimeSubscription({
    patterns: [{ pattern: '#statistic#', matchType: 'prefix' }],
    user: false,
    onUpdate: () => {
      openQuery.invalidate();
      resolvedQuery.invalidate();
    },
  });

  const dataMap = useMemo(() => {
    const map = new Map<string, number>();

    // Sum open + resolved counts for each username#severity key
    for (const item of [
      ...(openQuery.data ?? []),
      ...(resolvedQuery.data ?? []),
    ]) {
      const key = `${item.username}#${item.value}`;
      map.set(key, (map.get(key) ?? 0) + (item?.data?.count || 0));
    }

    return map;
  }, [openQuery.data, resolvedQuery.data]);

  const status: QueryStatus =
    openQuery.status === 'pending' || resolvedQuery.status === 'pending'
      ? 'pending'
      : openQuery.status === 'error' || resolvedQuery.status === 'error'
        ? 'error'
        : 'success';

  return {
    ...openQuery,
    status,
    dataMap,
  };
}
